import { YDatabase } from '@databases/collab/y-database';
import { currentHostFrameContext } from '@databases/database-state/state/host-frame-context';
import { FeatureFlagKey } from '@databases/feature-flags/flags';
import {
  featureFlagClientManager,
  getFlagValue as internalGetFlagValue,
} from '@databases/feature-flags/manager';
import { addOverrides } from '@databases/feature-flags/overrides/persisted';
import { logs } from '@databases/logs/activity-logger';
import { LogEntry } from '@databases/logs/types';
import { DatabaseJSON } from '@databases/model/database';
import { appStore } from '@databases/redux/store';
import databases from '@databases/testing/databases';
import { Database } from '@databases/types/database';
import {
  addCollabManager,
  buildDefaultValueConverters,
  buildDatabaseFromCollabAndContent,
} from '@databases/utils/redux/collab/actions';
import { convertToYjs } from '@databases/utils/redux/collab/convert-to-collab';
import { fakeDatabaseContent } from '@databases/utils/redux/databases/actions';
import {
  addDatabaseToList,
  markDatabaseAsLoaded,
} from '@databases/utils/redux/databases/actions';
import { IntlShape } from 'react-intl-next';
import { snapshot } from 'valtio';

export type SetDatabaseOptions =
  | {
      databaseName?: keyof typeof databases;
    }
  | { contentId: string; json: DatabaseJSON };

const validUUID = '4d555e81-af99-4dfa-9073-db4e49df4f6e';

const setFlagOverride = (flag: string, value: boolean) => {
  addOverrides({ [flag]: value });
  featureFlagClientManager.applyOverrides();
};

const setPageExportStatus = (status: boolean) => {
  currentHostFrameContext.data.dataSecurityPolicy = {
    ...currentHostFrameContext.data.dataSecurityPolicy,
    pageExport: status,
  };
};

const getFlagValue = (flag: FeatureFlagKey, defaultValue = false) => {
  return internalGetFlagValue(flag, defaultValue);
};

const setDatabase = async (options: SetDatabaseOptions) => {
  let database;
  let contentId;

  let doc;

  if ('json' in options) {
    doc = YDatabase.fromJSON(options.json, {
      getCurrentUser: () => {
        throw new Error();
      },
    });
    contentId = options.contentId;
    database = buildDatabaseFromCollabAndContent(
      {
        id: contentId,
        referenceId: '1234',
        space: { id: 12345, key: 'ABC', name: 'ABC' },
        type: 'database',
        title: 'my database',
        ...fakeDatabaseContent,
      },
      doc,
      false
    );
  } else {
    let databaseName: keyof typeof databases = 'emptyDatabase';
    if ('databaseName' in options && options.databaseName) {
      databaseName = options.databaseName;
    }

    const selectedDatabase = databases[databaseName];
    database = selectedDatabase.database;
    contentId = database.contentId ?? selectedDatabase.contentId;
    doc = convertToYjs(database, buildDefaultValueConverters(appStore.getState), {});
  }

  appStore.dispatch(addDatabaseToList(database as Database));
  appStore.dispatch(markDatabaseAsLoaded(contentId));

  await appStore.dispatch(
    addCollabManager(contentId, 1234, validUUID, {} as IntlShape, doc)
  );
  databaseState.setContentId(contentId);
};

export type DatabaseState = {
  reactAppIsReady: boolean;
  setContentId: (contentId: string) => void;
  setDatabase: (options: SetDatabaseOptions) => Promise<void>;
  getLogs: () => readonly LogEntry[];
  setFlagOverride: (flag: string, value: boolean) => void;
  getFlagValue: (flag: FeatureFlagKey, defaultValue?: boolean) => boolean;
  getDatabaseJSON: (contentId: string) => DatabaseJSON;
  setPageExportStatus: (status: boolean) => void;
};

export const databaseState: DatabaseState = {
  reactAppIsReady: false,
  setDatabase,
  setContentId: () => {},
  getLogs: () => snapshot(logs),
  setFlagOverride,
  getFlagValue,
  getDatabaseJSON: (contentId: string) =>
    appStore.getState().collab.byContentId[contentId].database.toJSON(),
  setPageExportStatus,
};
